import type { SneakPeekDay, SneakPeekLesson, SneakPeekPackageRow } from '@/services/api/sneakPeek'
import { MARKS } from '@/pages/sneak-peek/marks'
import { splitLessonIdentity } from '@/pages/sneak-peek/selection'

const WEEKDAY_SHORT = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So']

function clock(start: string | null) {
  return start ? start.slice(0, 5) : ''
}

function lessonSplit(columns: { entry_id: string }[], entryId: string) {
  return columns.filter((lesson) => lesson.entry_id === entryId).length > 1
}

function lessonIdentity(lesson: { entry_id: string; start_time: string | null }, columns: { entry_id: string }[]) {
  return splitLessonIdentity(lesson.entry_id, lesson.start_time, lessonSplit(columns, lesson.entry_id))
}

function covers(ids: string[], lesson: { entry_id: string; start_time: string | null }, columns: { entry_id: string }[]) {
  return ids.includes(splitLessonIdentity(lesson.entry_id, lesson.start_time, false))
    || ids.includes(splitLessonIdentity(lesson.entry_id, lesson.start_time, true))
    || ids.includes(lessonIdentity(lesson, columns))
}

function clockOf(clocks: Map<string, string>, id: string) {
  if (clocks.has(id)) return clocks.get(id) || ''
  for (const [key, value] of clocks) {
    if (key.startsWith(`${id}|`)) return value
  }
  return ''
}

function dayNote(iso: string) {
  const date = new Date(`${iso}T00:00:00`)
  const weekday = WEEKDAY_SHORT[(date.getDay() + 6) % 7]
  return `${weekday} ${date.getDate()}.${date.getMonth() + 1}.`
}

function alsoNote(row: SneakPeekPackageRow) {
  if (!row.other_days.length) return ''
  return `auch ${row.other_days.map(dayNote).join(', ')}`
}

function packageLine(day: SneakPeekDay, columns: SneakPeekLesson[]) {
  const clocks = new Map(columns.map((lesson) => [lessonIdentity(lesson, columns), clock(lesson.start_time)]))
  const groups = new Map<string, { who: string[], clocks: string[] }>()
  for (const row of day.packages || []) {
    const key = [...row.lesson_ids].sort().join('|')
    const times = row.lesson_ids.map((id) => clockOf(clocks, id)).filter(Boolean)
    const found = groups.get(key)
    const who = row.code || row.name
    if (found) found.who.push(who)
    else groups.set(key, { who: [who], clocks: times })
  }
  const parts = [...groups.values()]
    .sort((left, right) => (left.clocks[0] || '').localeCompare(right.clocks[0] || ''))
    .map((group) => `${group.who.join(', ')} ${group.clocks.join('+')}`)
  const open = (day.open_lesson_ids || []).map((id) => clockOf(clocks, id)).filter(Boolean)
  if (open.length) parts.push(`offen ${open.join('+')}`)
  return parts.join(' · ')
}

function rasterLessons(day: SneakPeekDay) {
  const ids = new Set<string>()
  for (const row of day.packages || []) {
    for (const id of row.lesson_ids) ids.add(id)
  }
  for (const id of day.open_lesson_ids || []) ids.add(id)
  return (day.lessons || [])
    .filter((lesson) => covers([...ids], lesson, day.lessons || []))
    .sort((left, right) => (left.start_time || '').localeCompare(right.start_time || ''))
}

export function CombinationRaster({ day }: { day: SneakPeekDay }) {
  if (day.cover_matrix !== true) return null
  const columns = rasterLessons(day)
  const packages = (day.packages || []).filter((row) =>
    columns.some((lesson) => !covers(row.lesson_ids, lesson, columns)),
  )
  if (!columns.length) return null
  const summary = packageLine({ ...day, packages }, columns)
  if (!packages.length && !summary) return null
  return (
    <div className="mt-1.5">
      {summary && day.combination !== MARKS.doesNotCover && day.combination !== MARKS.wholeDay && (
        <p aria-label="Pakete" className="break-words text-[10px] leading-snug text-gray-700">
          {summary}
        </p>
      )}
      {packages.length > 0 && (
      <div>
        <table aria-label="Kombination" className="w-max border-collapse text-[11px]">
          <thead>
            <tr>
              <th className="border-b border-gray-100 px-1 py-1 text-left" />
              {columns.map((lesson) => (
                <th key={lessonIdentity(lesson, columns)} className="min-w-[3.25rem] whitespace-nowrap border-b border-gray-100 px-1.5 py-1 text-center">
                  {clock(lesson.start_time)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {packages.map((row) => {
              const hits = new Set(row.lesson_ids)
              const note = alsoNote(row)
              return (
                <tr key={`${row.code}-${row.name}`}>
                  <td className="whitespace-nowrap border-b border-gray-100 px-1 py-1 text-left" title={note || undefined}>
                    {row.code && (
                      <span className="mr-1 rounded bg-gray-200 px-1 py-0.5 font-mono text-[11px] font-semibold text-gray-700">
                        {row.code}
                      </span>
                    )}
                    {row.name}
                    {note && <span className="block text-gray-500">{note}</span>}
                  </td>
                  {columns.map((lesson) => {
                    const can = covers([...hits], lesson, columns)
                    return (
                      <td
                        key={lessonIdentity(lesson, columns)}
                        aria-label={can ? 'kann' : 'kann nicht'}
                        className={`whitespace-nowrap border-b border-gray-100 px-1.5 py-1 text-center font-extrabold ${
                          can ? 'text-green-700' : 'text-rose-700'
                        }`}
                      >
                        {can ? '✓' : '—'}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      )}
    </div>
  )
}
